/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { toast } from "sonner";
import {
  X, Zap, ShieldCheck, Radar, Users, Award, Globe, ArrowRight, Loader2,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";

const PERKS = [
  {
    icon: Zap,
    title: "Instant notifications — up to 6× faster",
    body: "Mission Watch Protection watches your site throughout the day — even on weekends and holidays. If a donation page breaks at 2am, you'll know before the first morning donor tries.",
    accent: "brand",
  },
  {
    icon: Globe,
    title: "Whole-site audit — up to 150 pages",
    body: "Every event page, blog post, and volunteer form gets checked. Not just your homepage.",
    accent: "brand",
  },
  {
    icon: Award,
    title: "Verification badge for your website",
    body: "Public trust badge donors can click to see your live Mission Watch Score. Great for capital campaigns.",
    accent: "emerald",
  },
  {
    icon: Users,
    title: "Unlimited team members on reports",
    body: "Loop in your board, ED, and volunteers — everyone stays in the know.",
    accent: "emerald",
  },
  {
    icon: Radar,
    title: "5 manual deep checks / month",
    body: "On top of automatic Mission Watch Protection. Perfect right after you push a website change.",
    accent: "charcoal",
  },
];

const ACCENT_STYLES = {
  brand: "bg-brand/10 text-brand",
  emerald: "bg-emerald-100 text-emerald-600",
  charcoal: "bg-charcoal-900 text-emerald-400",
};

export default function UpgradeNudge({ open, onClose, reason, onUpgradeComplete }) {
  useEffect(() => {
    if (!open) return undefined;
    // Log impression exactly once when the modal opens
    api.post("/nudge/event", { event_type: "impression", reason }).catch(() => {});
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, reason]);

  if (!open) return null;

  const dismiss = () => {
    api.post("/nudge/event", { event_type: "dismiss", reason }).catch(() => {});
    onClose();
  };

  const upgrade = async () => {
    // Fire the click event BEFORE redirecting to Stripe so we still capture it.
    api.post("/nudge/event", { event_type: "click", reason }).catch(() => {});
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: "complete",
        origin_url: window.location.origin,
      });
      if (data.plan_change) {
        // Existing subscriber → server swapped the plan directly. Hand off to the
        // dashboard's success handler (shows the celebratory "Hourly surveillance is ON"
        // modal) instead of a hard page reload.
        if (typeof onUpgradeComplete === "function") {
          onUpgradeComplete(data);
          return;
        }
        toast.success(data.message || "You're now on Protection.");
        onClose();
        setTimeout(() => window.location.reload(), 700);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail), { duration: 8000 });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog" aria-modal="true" onClick={dismiss} data-testid="upgrade-nudge-modal">
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-premium overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={dismiss}
          className="absolute right-4 top-4 z-10 w-9 h-9 grid place-items-center rounded-full text-charcoal-400 hover:text-charcoal-900 hover:bg-charcoal-100 transition-colors"
          data-testid="upgrade-nudge-close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        {/* Header: dark hero */}
        <div className="bg-charcoal-900 text-white px-7 pt-9 pb-7 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dot-pulse" />
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
                {reason === "cap" ? "You've hit your monthly cap" : "A tip from Mission Watch"}
              </p>
            </div>
            <h2 className="mt-4 text-2xl sm:text-[26px] font-semibold tracking-tight leading-tight">
              Want us to <span className="text-emerald-400">keep watching while you sleep?</span>
            </h2>
            <p className="mt-2.5 text-sm text-white/60 leading-relaxed max-w-md">
              Protection checks your website, donation pages, and contact forms every hour and
              alerts you the moment something breaks — even on weekends and holidays.
            </p>
          </div>
        </div>

        {/* Perks */}
        <div className="px-7 py-6 space-y-4" data-testid="upgrade-perks">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="flex items-start gap-3.5">
                <div className={`w-9 h-9 rounded-xl shrink-0 grid place-items-center ${ACCENT_STYLES[p.accent]}`}>
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-charcoal-900 leading-tight">{p.title}</p>
                  <p className="text-[13px] text-charcoal-500 mt-1 leading-relaxed">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Price + CTA */}
        <div className="border-t border-black/5 px-7 py-5 bg-charcoal-50/50">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-400">Protection plan</p>
              <p className="text-2xl font-semibold tracking-tight text-charcoal-900">
                $39<span className="text-sm font-normal text-charcoal-400">/mo</span>
                <span className="ml-2 text-xs font-mono text-emerald-600">+$20 vs Checkup</span>
              </p>
            </div>
            <ShieldCheck className="w-8 h-8 text-emerald-500" strokeWidth={1.5} />
          </div>
          <button onClick={upgrade}
            className="w-full h-12 rounded-xl bg-brand text-white text-[15px] font-semibold hover:bg-brand-hover transition-colors inline-flex items-center justify-center gap-2 shadow-soft"
            data-testid="upgrade-nudge-cta">
            Turn On Mission Watch Protection <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={dismiss}
            className="w-full mt-2 h-10 rounded-xl text-sm text-charcoal-500 hover:text-charcoal-900 transition-colors"
            data-testid="upgrade-nudge-dismiss">
            Not now — my scans reset next month
          </button>
          <p className="mt-3 text-[11px] font-mono text-charcoal-400 text-center leading-relaxed">
            Prorated to today · Cancel anytime · You'll keep everything you already have.
          </p>
        </div>
      </div>
    </div>
  );
}

// Small helper: loader spinner (unused right now but exported for the button variants).
export function CTALoader() {
  return <Loader2 className="w-4 h-4 animate-spin" />;
}
